/**
 * Modelele de ASPECT ale aplicației — structura ecranului, nu culorile.
 *
 * Separarea e intenționată și e singura care ține sistemul explicabil:
 *   - `layout` (aici) = unde stă navigația, cât de dense sunt rândurile, ce colțuri și
 *     ce umbre au suprafețele, cu ce font se scriu titlurile. Se alege la nivel de FIRMĂ
 *     (implicitul pus de administrator) și poate fi suprascris de fiecare om pentru el.
 *     Se ține în baza de date, deci serverul randează direct forma corectă — fără salt.
 *   - `theme` (src/lib/themes.ts) = paleta. Rămâne preferință de dispozitiv (localStorage).
 *
 * Fiecare model recomandă o temă (`theme`), aplicată O DATĂ la comutare — după aceea omul
 * e liber să schimbe paleta din switcher. Nu blocăm culoarea: „Consolă pe Porțelan" e o
 * combinație validă, doar că nu e cea din care a plecat modelul.
 *
 * CSS-ul fiecărui model stă în globals.css (`:root[data-layout=...]`), pe variabilele `--l-*`;
 * „Direcții” își ține regulile proprii în `components/app-shell-directii.css`.
 */
import type { ThemeId } from "@/lib/themes";

/* Lista e SURSA, tipul se derivă din ea: așa un model nou se adaugă într-un singur loc,
   iar validările care o consumă (z.enum din acțiuni) rămân sincronizate prin compilator. */
export const LAYOUT_IDS = ["tablou", "consola", "cifra", "directii"] as const;
export type LayoutId = (typeof LAYOUT_IDS)[number];

export const DEFAULT_LAYOUT: LayoutId = "tablou";

export type LayoutDef = {
  id: LayoutId;
  label: string;
  hint: string;
  /** Pentru cine e gândit — text arătat în ecranul de alegere. */
  forWhom: string;
  /** Tema care merge cu modelul; se aplică la comutare, nu blochează switcher-ul. */
  theme: ThemeId;
  /** Navigația: bară de module sus, rail vertical pliabil, sau rail de DIRECȚII (doar
   *  iconuri) cu panou de secțiuni. Decide structura din AppShell. */
  nav: "topbar" | "rail" | "directii";
};

export const LAYOUTS: LayoutDef[] = [
  {
    id: "tablou",
    label: "Tablou",
    hint: "Module în bara de sus, tabele dense, colțuri drepte",
    forWhom: "Pentru echipe care lucrează toată ziua în liste și documente. Cel mai mult conținut pe ecran.",
    theme: "portelan",
    nav: "topbar",
  },
  {
    id: "consola",
    label: "Consolă",
    hint: "Întunecat, compact, cu scurtături pe taste",
    forWhom: "Pentru cine stă ore în aplicație și preferă tastatura. Rânduri strânse, cifre monospațiate.",
    theme: "noapte",
    nav: "rail",
  },
  {
    id: "cifra",
    label: "Cifra",
    hint: "Aerisit, cifre mari, ținte generoase",
    forWhom: "Pentru management și pentru telefon: puține cifre, mari, cu acțiunea evidentă.",
    theme: "portelan",
    nav: "rail",
  },
  {
    id: "directii",
    label: "Direcții",
    hint: "Rail de direcții cu panou de secțiuni — ca într-un ERP clasic",
    forWhom: "Pentru cine vine dintr-un ERP: zece direcții de lucru în stânga, secțiunile lor într-un panou, „+ Nou” și paleta în bara de sus.",
    theme: "clasic",
    nav: "directii",
  },
];

export function layoutDef(id: string | null | undefined): LayoutDef {
  return LAYOUTS.find((l) => l.id === id) ?? LAYOUTS.find((l) => l.id === DEFAULT_LAYOUT)!;
}

/** Normalizează o valoare venită din DB/intrare. Necunoscut → null (adică „moștenește"). */
export function asLayoutId(v: string | null | undefined): LayoutId | null {
  return (LAYOUT_IDS as readonly string[]).includes(v ?? "") ? (v as LayoutId) : null;
}
